/**
 * Phase 10 R2 — Pure Job-Labor Computation Helper
 *
 * ⚠️  CONCEPT BOUNDARY (do not collapse):
 *
 *   Labor sell ≠ technician pay ≠ labor cost.
 *
 * This module computes only the **applied labor sell amount** for a single job
 * line. It is consumed exclusively by the Phase 10 labor module read/write path.
 *
 * ⚠️  `sold_minutes` IS A SELL-SIDE SNAPSHOT — IT IS NOT:
 *   • `predicted_minutes` (Book/Job-Catalog minutes — scheduler authority)
 *   • `minutes_override`  (per-ticket scheduler override)
 *   • `opti_time` / `opti_finish` (Wall time / Wall finish — operational)
 *   • reference time, technician time, or pay time
 *
 * It must never be read by the scheduler, cascade, optiTime calculator, or
 * any pay/cost engine. Sell-side only.
 *
 * Formula:
 *   labor_sell_amount = (sold_minutes / 60) * labor_rate_applied
 *
 * Override semantics (R2 ships visibility + reversibility ONLY):
 *   • `labor_rate_resolved` = the resolver's output (preserved when overridden)
 *   • `labor_rate_applied`  = the rate actually used in $ math
 *   • `labor_rate_overridden` = true when applied != resolved by manual edit
 *   • Override reason / actor / timestamp / history → deferred to R5.
 */

import {
  resolveLaborRate,
  type ShopLaborRates,
  type LaborRateSource,
} from './resolveLaborRate';

export interface ComputeJobLaborInput {
  /** Effective minutes for the job (e.g. ticket_jobs.minutes_override ?? jobs.standard_minutes). */
  effectiveMinutes: number;
  /** Job category — fed to resolveLaborRate. */
  jobCategory: string | null | undefined;
  /** Shop-level labor rates from useShopLaborRates. */
  shopRates: ShopLaborRates;
  /** Optional manual override; when present, applied rate = override.rate. */
  override?: { rate: number } | null;
}

export interface ComputedJobLabor {
  sold_minutes: number;
  sold_hours: number;
  labor_rate_applied: number;
  labor_rate_resolved: number;
  labor_rate_source: LaborRateSource | 'override';
  labor_rate_reason: string;
  labor_sell_amount: number;
  labor_rate_overridden: boolean;
}

/** Round dollars to 2dp. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function computeJobLabor(input: ComputeJobLaborInput): ComputedJobLabor {
  const sold_minutes = Math.max(0, Math.round(input.effectiveMinutes || 0));
  const sold_hours = sold_minutes / 60;

  const resolved = resolveLaborRate(input.shopRates, input.jobCategory ?? null);

  const isOverride = !!(input.override && input.override.rate > 0);
  const applied = isOverride ? Number(input.override!.rate) : resolved.rate;

  const labor_sell_amount = round2(sold_hours * applied);

  return {
    sold_minutes,
    sold_hours,
    labor_rate_applied: round2(applied),
    labor_rate_resolved: round2(resolved.rate),
    labor_rate_source: isOverride ? 'override' : resolved.source,
    labor_rate_reason: isOverride
      ? `Manual override: $${applied.toFixed(2)}/hr (resolved was $${resolved.rate.toFixed(2)}/hr — ${resolved.reason})`
      : resolved.reason,
    labor_sell_amount,
    labor_rate_overridden: isOverride,
  };
}

/**
 * Build the partial ticket_jobs payload for a labor write.
 * Used by every persistence call site to keep column shape consistent.
 */
export function laborColumnsForUpsert(c: ComputedJobLabor) {
  return {
    sold_minutes: c.sold_minutes,
    labor_rate_applied: c.labor_rate_applied,
    labor_rate_resolved: c.labor_rate_resolved,
    labor_rate_source: c.labor_rate_source,
    labor_rate_reason: c.labor_rate_reason,
    labor_sell_amount: c.labor_sell_amount,
    labor_rate_overridden: c.labor_rate_overridden,
  };
}
